import * as THREE from 'three'
import { clone as cloneSkinned } from 'three/examples/jsm/utils/SkeletonUtils.js'
import type { PrototypeGltf } from './loader'
import { withClipPrefix, PROTOTYPE_SKELETON, type PrototypeClip } from './clips'

/** Which hand to attach a held object to. */
export type Hand = 'left' | 'right'

/**
 * Offset for an object attached to a hand bone. Position and scale are in world
 * metres (attachToHand counters the rig's 100x bone scale for you). Rotation is
 * relative to the hand bone's rest frame.
 */
export interface AttachOffset {
  /** Grip offset from the hand, in world metres. */
  position?: [number, number, number]
  /** Euler XYZ in radians, in the hand bone's local frame. */
  rotation?: [number, number, number]
  /** Uniform scale in world metres (1 = author-scale). */
  scale?: number
}

/**
 * Per-instance reskin. The base mesh has two materials (`M_Main` body and
 * `M_Joints` accent); set either to recolour without touching other instances.
 * This is the "many skins" half of "one rig, many skins".
 */
export interface PrototypeSkin {
  bodyColor?: THREE.ColorRepresentation
  accentColor?: THREE.ColorRepresentation
}

/** Options for playing a clip. */
export interface PrototypePlayOptions {
  /** Crossfade duration in seconds from the current clip. Default 0.2. */
  fade?: number
  /** Loop the clip. Default true; set false for one-shots (death, reload). */
  loop?: boolean
  /** Hold the final frame when a one-shot finishes. Default false. */
  clampWhenFinished?: boolean
  /** Playback rate. Default 1. */
  timeScale?: number
  /**
   * Force the clip to reset and restart from frame 0 even when it is already
   * `current`. Without this, play() is a same-clip no-op (by design, so a
   * looping locomotion clip is not re-triggered every frame it is re-selected)
   * - but a repeating one-shot action (the full-auto fire pose re-issued every
   * ~80-100ms by a sustained SMG/assault-rifle burst) needs exactly the
   * opposite: each new fire pulse must restart the clip from the top, or the
   * mixer's weight collapses to zero after the first shot and the character
   * appears frozen for the rest of the burst. Default false.
   */
  restart?: boolean
}

/** A cloned, animatable prototype ready to add to a scene. */
export interface PrototypeInstance {
  /** Scene node to add to the world. Position/rotate this from game state. */
  readonly root: THREE.Object3D
  /** The clip currently playing, or null before the first play(). */
  readonly current: PrototypeClip | null
  /** Crossfade to a clip. No-op if it is already the current clip. */
  play(clip: PrototypeClip, opts?: PrototypePlayOptions): void
  /** Advance the animation mixer by dt seconds. Call once per frame. */
  update(dtSec: number): void
  /**
   * The clip's authored duration in seconds, or null if it is not present in
   * this rig's animation set. Lets a caller compute a `timeScale` that makes
   * a fixed-length clip (for example the single shared reload gesture) span a
   * different, gameplay-driven duration (each weapon's own reloadMs).
   */
  getClipDuration(clip: PrototypeClip): number | null
  /** Find a skeleton node (for example a hand bone) by name. */
  getBone(name: string): THREE.Object3D | null
  /**
   * Parent an object (a weapon, torch, prop) to a hand bone so the rig's
   * animations carry it. The mesh stays your own; the rig just animates the
   * hand holding it. Returns the hand bone, or null if it is missing.
   */
  attachToHand(object: THREE.Object3D, hand?: Hand, offset?: AttachOffset): THREE.Object3D | null
  /** Scale so the bind-pose height equals targetMeters, feet at local y=0. */
  fitHeight(targetMeters: number): void
  /** Release per-instance materials and detach from the mixer. */
  dispose(): void
}

const MAIN_MATERIAL = 'M_Main'
const ACCENT_MATERIAL = 'M_Joints'

function recolour(material: THREE.Material, skin: PrototypeSkin): void {
  const std = material as THREE.MeshStandardMaterial
  if (!std.color) return
  if (material.name === MAIN_MATERIAL && skin.bodyColor != null) std.color.set(skin.bodyColor)
  if (material.name === ACCENT_MATERIAL && skin.accentColor != null) std.color.set(skin.accentColor)
}

/**
 * Clone the shared rig into an independent, animatable instance. Geometry and
 * skeleton bind data are shared with the source parse (cheap); materials are
 * cloned per instance only when a skin is supplied, so recolouring one agent
 * never bleeds into another. SkinnedMesh does not GPU-instance, so this clone
 * per agent is the expected approach.
 *
 * Kept deliberately identical to its sibling copy at
 * sunder/sstk-sunder-web/src/prototype/src/instance.ts (see that file's own
 * header). @sstk/character-schema cannot host this itself (it is explicitly
 * types-only, see its own AGENTS.md), so until a dedicated shared runtime
 * package exists, keeping the two files byte-identical - and porting any fix
 * or feature to both - is what stands in for one.
 */
export function createPrototypeInstance(gltf: PrototypeGltf, skin?: PrototypeSkin): PrototypeInstance {
  const root = cloneSkinned(gltf.scene)
  const ownedMaterials: THREE.Material[] = []

  if (skin) {
    root.traverse((obj: THREE.Object3D) => {
      const mesh = obj as THREE.Mesh
      if (!mesh.isMesh) return
      const source = Array.isArray(mesh.material) ? mesh.material : [mesh.material]
      const cloned = source.map((mat: THREE.Material) => {
        const copy = mat.clone()
        recolour(copy, skin)
        ownedMaterials.push(copy)
        return copy
      })
      mesh.material = Array.isArray(mesh.material) ? cloned : cloned[0]!
    })
  }

  const mixer = new THREE.AnimationMixer(root)
  const actions = new Map<PrototypeClip, THREE.AnimationAction>()
  let current: PrototypeClip | null = null
  let currentAction: THREE.AnimationAction | null = null

  function actionFor(clip: PrototypeClip): THREE.AnimationAction | null {
    const existing = actions.get(clip)
    if (existing) return existing
    const raw = THREE.AnimationClip.findByName(gltf.animations, withClipPrefix(clip))
    if (!raw) return null
    const action = mixer.clipAction(raw)
    actions.set(clip, action)
    return action
  }

  return {
    root,
    get current() {
      return current
    },

    play(clip, opts = {}) {
      if (clip === current && !opts.restart) {
        if (opts.timeScale != null && currentAction) currentAction.timeScale = opts.timeScale
        return
      }
      const next = actionFor(clip)
      if (!next) return
      const fade = opts.fade ?? 0.2

      next.reset()
      next.enabled = true
      next.setLoop(opts.loop === false ? THREE.LoopOnce : THREE.LoopRepeat, Infinity)
      next.clampWhenFinished = opts.clampWhenFinished ?? false
      next.timeScale = opts.timeScale ?? 1
      next.fadeIn(fade)
      next.play()

      if (currentAction && currentAction !== next) currentAction.fadeOut(fade)
      currentAction = next
      current = clip
    },

    update(dtSec) {
      mixer.update(dtSec)
    },

    getClipDuration(clip) {
      const raw = THREE.AnimationClip.findByName(gltf.animations, withClipPrefix(clip))
      return raw ? raw.duration : null
    },

    getBone(name) {
      return root.getObjectByName(name) ?? null
    },

    attachToHand(object, hand = 'right', offset) {
      const boneName = PROTOTYPE_SKELETON.handBones[hand]
      const bone = root.getObjectByName(boneName)
      if (!bone) return null
      // The hand bone lives inside the rig's 100x-scaled subtree, so a child in
      // metres would be ~100x too big. Counter that automatically: offset.scale
      // and offset.position are then expressed in world metres (default 1m).
      root.updateMatrixWorld(true)
      const worldScale = new THREE.Vector3()
      bone.matrixWorld.decompose(new THREE.Vector3(), new THREE.Quaternion(), worldScale)
      const boneScale = (worldScale.x + worldScale.y + worldScale.z) / 3 || 1
      const inv = 1 / boneScale
      object.scale.setScalar((offset?.scale ?? 1) * inv)
      if (offset?.position) object.position.fromArray(offset.position).multiplyScalar(inv)
      if (offset?.rotation) object.rotation.set(offset.rotation[0], offset.rotation[1], offset.rotation[2])
      bone.add(object)
      return bone
    },

    fitHeight(targetMeters) {
      root.scale.setScalar(1)
      root.updateMatrixWorld(true)
      const box = new THREE.Box3().setFromObject(root)
      const height = box.max.y - box.min.y
      if (height > 0) {
        const factor = targetMeters / height
        root.scale.setScalar(factor)
        root.updateMatrixWorld(true)
        const scaled = new THREE.Box3().setFromObject(root)
        root.position.y -= scaled.min.y
      }
    },

    dispose() {
      mixer.stopAllAction()
      mixer.uncacheRoot(root as THREE.Object3D)
      // Geometry and skeleton are shared with the source parse, so only the
      // per-instance cloned materials are ours to release.
      for (const material of ownedMaterials) material.dispose()
    },
  }
}
